import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useLanguage, COUNTRIES, getConversionHeaders, clearConversionSource, hasTrackableSource } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, Building2, Loader2, Home } from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Whitelist of safe relative return paths (prevents open-redirect)
  const safeNext = (raw) => {
    if (!raw) return null;
    try {
      const decoded = decodeURIComponent(raw);
      if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
    } catch (_) {}
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t('auth.loginSuccess'));
      const next = safeNext(searchParams.get('next'));
      if (next) {
        navigate(next, { replace: true });
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'company') {
        navigate('/company/dashboard');
      } else {
        navigate('/worker/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <Link to="/" className="flex items-center justify-center gap-2 text-2xl font-bold text-brand mb-2">
            <Home className="h-5 w-5" />
            KEIKKA
          </Link>
          <CardTitle className="text-2xl font-bold">{t('auth.login')}</CardTitle>
          <CardDescription>{t('auth.enterCredentials')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-end -mt-1">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-brand hover:underline underline-offset-2"
                data-testid="forgot-password-link"
              >
                {t('auth.forgotPasswordLink')}
              </Link>
            </div>
            <Button type="submit" className="w-full" data-testid="login-submit-btn" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('auth.login')}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-4">
            {t('auth.dontHaveAccount')}{' '}
            <Link
              to={`/register${searchParams.get('next') ? `?next=${encodeURIComponent(searchParams.get('next'))}` : ''}`}
              className="text-brand hover:underline"
              data-testid="register-link"
            >
              {t('auth.register')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export const RegisterPage = () => {
  const { register } = useAuth();
  const { t, language, availableLanguages, languageNames } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  // Get role from URL query param, default to 'worker'
  const initialRole = searchParams.get('role') === 'company' ? 'company' : 'worker';
  const [role, setRole] = useState(initialRole);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nickname: '',
    company_name: '',
    registry_country: '',
    preferred_language: language // Use current UI language as default
  });

  // Update preferred_language when UI language changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, preferred_language: language }));
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...formData,
        role: role
      };
      if (role === 'worker') {
        delete data.company_name;
        delete data.registry_country;
      }
      const user = await register(data);
      toast.success(t('auth.registerSuccess'));
      // Honor ?next= to return the user to their intended destination
      const nextRaw = searchParams.get('next');
      const safeNext = (() => {
        if (!nextRaw) return null;
        try {
          const d = decodeURIComponent(nextRaw);
          return d.startsWith('/') && !d.startsWith('//') ? d : null;
        } catch (_) { return null; }
      })();
      if (safeNext) {
        navigate(safeNext, { replace: true });
      } else if (user.role === 'company') {
        navigate('/company/dashboard');
      } else {
        navigate('/worker/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 py-12">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-1 text-center">
          <Link to="/" className="flex items-center justify-center gap-2 text-2xl font-bold text-brand mb-2">
            <Home className="h-5 w-5" />
            KEIKKA
          </Link>
          <CardTitle className="text-2xl font-bold">{t('auth.register')}</CardTitle>
          <CardDescription>{t('auth.createAccount')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={role} onValueChange={setRole} className="mb-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="worker" data-testid="register-worker-tab" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                {t('auth.registerAsWorker')}
              </TabsTrigger>
              <TabsTrigger value="company" data-testid="register-company-tab" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {t('auth.registerAsCompany')}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                data-testid="register-email-input"
                placeholder="name@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                data-testid="register-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="nickname">{t('auth.nickname')}</Label>
              <Input
                id="nickname"
                data-testid="register-nickname-input"
                placeholder={t('auth.displayName')}
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                required
                minLength={2}
              />
            </div>

            {role === 'company' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="company_name">{t('auth.companyName')}</Label>
                  <Input
                    id="company_name"
                    data-testid="register-company-name-input"
                    placeholder={t('auth.companyName')}
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="registry_country">{t('auth.registryCountry')}</Label>
                  <Select
                    value={formData.registry_country}
                    onValueChange={(val) => setFormData({ ...formData, registry_country: val })}
                  >
                    <SelectTrigger data-testid="register-country-select">
                      <SelectValue placeholder={t('auth.selectCountry')} />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {t(country.nameKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>{t('auth.preferredLanguage')}</Label>
              <Select
                value={formData.preferred_language}
                onValueChange={(val) => setFormData({ ...formData, preferred_language: val })}
              >
                <SelectTrigger data-testid="register-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableLanguages.map((lang) => (
                    <SelectItem key={lang} value={lang}>
                      {languageNames[lang]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full" data-testid="register-submit-btn" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('auth.register')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-4">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-brand hover:underline" data-testid="login-link">
              {t('auth.login')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
